import React, { useEffect, useCallback, useState, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  FormHelperText,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Fab,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import VideoComponent from "../videoplayer/VideoComponent";
import { useVideosQuery } from "../stores/tanstack/queries/useVideosQuery";
import { useAuthStore } from "../stores/zustand";

/**
 * VideoLibraryComponent
 *
 * Main video library interface displaying videos in a card grid with:
 * - Tab switching between all videos and physio-specific videos (for physio users)
 * - Multi-select preference filter to narrow results by exercise type
 * - Video card grid with hover effects and click-to-play functionality
 * - Embedded video player for inline playback with playlist navigation
 * - Auto-open feature: if libraryId prop is provided, automatically opens that video
 * - Loading, error, and empty states with appropriate messaging
 * - Refresh FAB for manual cache refresh
 *
 * Video data is cached via TanStack Query and memoized for performance.
 * Auto-open logic uses a ref to prevent re-opening after user closes the player.
 *
 * @param {string} libraryId - Optional video ID to auto-open when component loads
 * @returns {JSX.Element} Video library with cards, filters, and player
 */
const VideoLibraryComponent = ({ libraryId, preferenceName }) => {
  // Translation and auth context
  const { t } = useTranslation();

  // Query hook for videos (all and physio-specific lists)
  const {
    data: videosData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useVideosQuery();

  // Extract and memoize video lists to prevent unnecessary recalculations
  const { allVideos, physioVideos } = useMemo(
    () => ({
      allVideos: videosData?.allVideos || [],
      physioVideos: videosData?.physioVideos || [],
    }),
    [videosData?.allVideos, videosData?.physioVideos],
  );

  // Track active tab: "all" shows all/filtered videos, "physio" shows physio-only videos
  const [activeTab, setActiveTab] = useState("all");
  const defaultTabSetRef = React.useRef(false);

  // Preference filter: selectedPreferences committed, tempSelectedPreferences for dropdown
  // wasEverSelected prevents showing error when user hasn't made a selection yet
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [tempSelectedPreferences, setTempSelectedPreferences] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [wasEverSelected, setWasEverSelected] = useState(false);

  // Track currently selected video and playlist for VideoComponent
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [playlistVideos, setPlaylistVideos] = useState([]);

  // Determine if user is physio to show physio tab
  const { currentProduct } = useAuthStore();
  const isPhysioUser = currentProduct === "physio_user";

  // Set default tab for physio_user based on DD video availability (runs once after load)
  useEffect(() => {
    if (!isPhysioUser || isLoading || isFetching || defaultTabSetRef.current) return;
    defaultTabSetRef.current = true;
    setActiveTab(physioVideos.length > 0 ? "physio" : "all");
  }, [isPhysioUser, isLoading, isFetching, physioVideos.length]);

  // Ref to track which video ID was auto-opened, preventing duplicate opens after close
  const autoOpenedRef = React.useRef(null);
  // Ref for smooth scrolling to video player
  const videoPlayerRef = React.useRef(null);

  // Extract unique preferences from all videos (excluding "Random" type)
  // Used for filter dropdown options
  const availablePreferences = useMemo(() => {
    const prefMap = new Map();

    if (!Array.isArray(allVideos)) return [];

    allVideos.forEach((video) => {
      video.preferences?.forEach((pref) => {
        if (pref.id !== 12 && !prefMap.has(pref.id)) {
          prefMap.set(pref.id, pref);
        }
      });
    });

    return Array.from(prefMap.values());
  }, [allVideos]);

  // Auto-filter by preference when navigating from notification
  useEffect(() => {
    if (!preferenceName || availablePreferences.length === 0) return;

    const matchingPref = availablePreferences.find(
      (p) => p.name.toLowerCase() === preferenceName.toLowerCase().trim(),
    );

    if (matchingPref) {
      setSelectedPreferences([matchingPref.id]);
      setTempSelectedPreferences([matchingPref.id]);
      setWasEverSelected(true);
    }
  }, [preferenceName, availablePreferences]);

  // Apply preference filter: if no preferences selected, show all
  const filteredVideos = useMemo(() => {
    if (!Array.isArray(allVideos)) return [];

    if (selectedPreferences.length === 0) {
      return allVideos;
    }

    return allVideos.filter((video) =>
      video.preferences?.some((pref) => selectedPreferences.includes(pref.id)),
    );
  }, [allVideos, selectedPreferences]);

  // Find video matching libraryId prop (search both all and physio lists)
  // Compares as strings to handle various ID formats
  const libraryMatchedVideo = useMemo(() => {
    if (!libraryId) return null;
    const libIdStr = String(libraryId);
    const findIn = (arr) =>
      Array.isArray(arr)
        ? arr.find((v) => String(v.externalId) === libIdStr)
        : null;

    return findIn(allVideos) || findIn(physioVideos) || null;
  }, [libraryId, allVideos, physioVideos]);

  // Handle preference selection in dropdown (supports "select all" option)
  const handlePreferenceChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      if (tempSelectedPreferences.length === availablePreferences.length) {
        setTempSelectedPreferences([]);
      } else {
        setTempSelectedPreferences(availablePreferences.map((p) => p.id));
      }
    } else {
      setTempSelectedPreferences(value);
    }
  };

  // Open dropdown: copy selected to temp
  const handleOpenSelect = () => {
    setTempSelectedPreferences(selectedPreferences);
    setSelectOpen(true);
  };

  // Close dropdown: commit temp selections to state
  // Only applies selection if at least one preference is chosen or nothing was selected yet
  const handleCloseSelect = () => {
    if (tempSelectedPreferences.length > 0 || !wasEverSelected) {
      setSelectedPreferences(tempSelectedPreferences);
      setWasEverSelected(true);
    }
    setSelectOpen(false);
  };

  // Reset all filters
  const resetFilters = () => {
    setSelectedPreferences([]);
    setTempSelectedPreferences([]);
  };

  // Get human-readable preference names for selected IDs
  const getPreferenceDisplayNames = (selectedIds) => {
    return availablePreferences
      .filter((pref) => selectedIds.includes(pref.id))
      .map((pref) => pref.name);
  };

  // Smooth scroll to video player with custom animation
  const scrollToVideoPlayer = useCallback(() => {
    setTimeout(() => {
      if (!videoPlayerRef.current) return;

      const targetPosition =
        videoPlayerRef.current.getBoundingClientRect().top + window.scrollY;
      const startPosition = window.scrollY;
      const distance = targetPosition - startPosition;
      const duration = 800; // ms - adjust for slower/faster scroll
      let startTime = null;

      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        window.scrollTo(0, startPosition + distance * easeInOutCubic(progress));

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }, 100);
  }, []);

  // Open video player with selected video and build playlist for navigation
  // Playlist depends on active tab: physio videos or filtered videos
  const openVideoPlayer = useCallback(
    (video) => {
      if (!video) return;

      setSelectedVideoId(video.externalId);

      const playlist =
        activeTab === "physio"
          ? Array.isArray(physioVideos)
            ? physioVideos
            : []
          : filteredVideos;

      setPlaylistVideos(playlist);
      setShowVideoPlayer(true);

      // Smooth scroll to video player
      scrollToVideoPlayer();
    },
    [activeTab, physioVideos, filteredVideos, scrollToVideoPlayer],
  );

  // Close video player and clear related state
  const closeVideoPlayer = () => {
    setShowVideoPlayer(false);
    setSelectedVideoId(null);
    setPlaylistVideos([]);
  };

  // UI state flags for conditional rendering
  const hasPreferenceFilter = selectedPreferences.length > 0;
  const hasFilterError = wasEverSelected && selectedPreferences.length === 0;
  const filteredVideosCount = filteredVideos.length;
  const totalVideos = allVideos.length;

  // Base videos to display based on active tab
  const displayVideosBase =
    activeTab === "physio"
      ? Array.isArray(physioVideos)
        ? physioVideos
        : []
      : filteredVideos;

  // If libraryId resolved to a video, promote it to first position in display
  // Enables auto-open to show most relevant video first
  const displayVideos = useMemo(() => {
    if (!libraryMatchedVideo) return displayVideosBase;

    const isInBase =
      Array.isArray(displayVideosBase) &&
      displayVideosBase.some(
        (v) => String(v.externalId) === String(libraryMatchedVideo.externalId),
      );

    if (!isInBase) return displayVideosBase;

    return [
      libraryMatchedVideo,
      ...displayVideosBase.filter(
        (v) => String(v.externalId) !== String(libraryMatchedVideo.externalId),
      ),
    ];
  }, [displayVideosBase, libraryMatchedVideo]);

  // Auto-open matched video when libraryId resolves
  // Uses ref to track which video was auto-opened, preventing re-open after close
  useEffect(() => {
    if (!libraryMatchedVideo) {
      // if libraryId cleared or videos not loaded yet, reset the ref so future ids can auto-open
      autoOpenedRef.current = null;
      return;
    }

    const matchedId = String(libraryMatchedVideo.externalId);

    // If we've already auto-opened this exact video, don't auto-open again
    if (autoOpenedRef.current === matchedId) return;

    // If the player is already opened with the same video, mark it as auto-opened and bail out
    if (showVideoPlayer && String(selectedVideoId) === matchedId) {
      autoOpenedRef.current = matchedId;
      return;
    }

    // open the player for the matched video and record that we did so
    openVideoPlayer(libraryMatchedVideo);
    autoOpenedRef.current = matchedId;
  }, [libraryMatchedVideo, openVideoPlayer, showVideoPlayer, selectedVideoId]);

  // Render preference filter dropdown (only shown on "all" tab)
  const renderPreferenceFilter = () => {
    if (activeTab !== "all") return null;

    return (
      <Box sx={{ position: "relative", marginBottom: 3 }}>
        <FormControl
          fullWidth
          className="preferenceSelector"
          error={hasFilterError}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "white",
              borderColor: "white",
              "& fieldset": {
                borderColor: "white",
              },
              "&:hover fieldset": {
                borderColor: "rgba(255, 255, 255, 0.8)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "white",
              },
            },
            "& .MuiInputLabel-root": {
              color: "white",
              backgroundColor: "#00023a",
              padding: "0 8px",
            },
          }}
        >
          {selectedPreferences.length === 0 && !selectOpen && (
            <InputLabel sx={{ color: "white", backgroundColor: "#00023a" }}>
              {t("preference")}
            </InputLabel>
          )}

          <Select
            multiple
            value={tempSelectedPreferences}
            onChange={handlePreferenceChange}
            onOpen={handleOpenSelect}
            onClose={handleCloseSelect}
            open={selectOpen}
            renderValue={(selected) => {
              const names = getPreferenceDisplayNames(selected);
              return names.length > 0 ? names.join(", ") : "";
            }}
            sx={{ color: "white" }}
          >
            <MenuItem key="all" value="all">
              <Checkbox
                checked={
                  tempSelectedPreferences.length === availablePreferences.length
                }
                indeterminate={
                  tempSelectedPreferences.length > 0 &&
                  tempSelectedPreferences.length < availablePreferences.length
                }
              />
              {t("selectAll")}
            </MenuItem>

            {availablePreferences.map((pref) => (
              <MenuItem key={pref.id} value={pref.id}>
                <Checkbox
                  checked={tempSelectedPreferences.indexOf(pref.id) > -1}
                />
                {pref.name}
              </MenuItem>
            ))}

            <Box
              sx={{
                textAlign: "right",
                padding: "10px",
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Button
                onClick={handleCloseSelect}
                sx={{
                  color: "white",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {t("okay")}
              </Button>
            </Box>
          </Select>

          {hasFilterError && (
            <FormHelperText sx={{ color: "error.main" }}>
              {t("pleaseChooseAtleastOneExercise")}
            </FormHelperText>
          )}
        </FormControl>

        {hasPreferenceFilter && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.7)" }}
            >
              {t("showing")} {filteredVideosCount} {t("of")} {totalVideos}{" "}
              {t("videos")}
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              sx={{
                color: "white",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {t("clearFilters")}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Render video card grid with hover effects and click handlers
  const renderVideoGrid = () => (
    <Grid container spacing={2}>
      {displayVideos && displayVideos.length > 0 ? (
        displayVideos.map((video) => (
          <Grid
            item
            key={video.id}
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Card
              onClick={() => openVideoPlayer(video)}
              sx={{
                height: "350px",
                cursor: "pointer",
                backgroundColor: "rgba(0, 0, 0, 0)",
                transition:
                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              <CardMedia
                component="img"
                alt={video.title}
                height="200"
                image={`${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${video.externalId}`}
                loading="lazy"
                sx={{
                  objectFit: "cover",
                  transition: "opacity 0.2s ease-in-out",
                  "&:hover": {
                    opacity: 0.9,
                  },
                }}
              />
              <CardContent
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  backgroundColor: "transparent",
                  color: "white",
                  padding: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "0.75rem",
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    {video.preferences
                      ?.filter((pref) => pref.name !== "Random")
                      .map((pref) => pref.name)
                      .join(", ")}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {video.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.875rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4,
                    }}
                  >
                    {video.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Typography color="white" align="center">
            {t("noVideosFound")}
          </Typography>
        </Grid>
      )}
    </Grid>
  );

  // Render loading state with spinner and message
  const renderLoadingState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "60vh",
        gap: 2,
      }}
    >
      <CircularProgress size={60} sx={{ color: "white" }} />
      <Typography variant="h6" sx={{ color: "white" }}>
        {t("loadingVideos")}
      </Typography>
    </Box>
  );

  // Render error alert with retry button
  const renderErrorState = () => (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            {t("retry")}
          </Button>
        }
      >
        {error?.message}
      </Alert>
    </Box>
  );

  // Main layout with tabs, filters, player, grid, and empty states
  return (
    <Container
      ref={videoPlayerRef}
      sx={{ height: "calc(100vh - 200px)", paddingTop: 10 }}
    >
      {!isLoading && !isFetching && !error && (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Tab navigation: All Videos and Physio Videos (if user is physio) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
              <Typography
                variant="h5"
                onClick={() => setActiveTab("all")}
                sx={{
                  color:
                    activeTab === "all" ? "white" : "rgba(255,255,255,0.6)",
                  cursor: "pointer",
                  textTransform: "none",
                  fontWeight: activeTab === "all" ? 600 : 400,
                  mr: 1,
                  textDecoration: activeTab === "all" ? "underline" : "none",
                }}
              >
                {t("allVideos")}
              </Typography>

              {isPhysioUser && (
                <>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", mx: 0.5 }}>
                    |
                  </Typography>
                  <Typography
                    variant="h5"
                    onClick={() => setActiveTab("physio")}
                    sx={{
                      color:
                        activeTab === "physio"
                          ? "white"
                          : "rgba(255,255,255,0.6)",
                      cursor: "pointer",
                      textTransform: "none",
                      fontWeight: activeTab === "physio" ? 600 : 400,
                      textDecoration:
                        activeTab === "physio" ? "underline" : "none",
                      ml: 1,
                    }}
                  >
                    {t("Physio Videos") || "Physio Videos"}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Video count display */}
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
          >
            {displayVideos.length}{" "}
            {displayVideos.length === 1 ? t("video") : t("videos")}
          </Typography>
        </Box>
      )}

      {/* Error state with retry option */}
      {error && renderErrorState()}

      {/* Preference filter dropdown (all tab only) */}
      {!isLoading && !isFetching && !error && renderPreferenceFilter()}

      {/* Video player when active */}
      {showVideoPlayer && (
        <Box sx={{ padding: 1, mb: 3 }}>
          <VideoComponent
            videoId={selectedVideoId}
            onClose={closeVideoPlayer}
            playlist={playlistVideos}
            autoplay={true}
            isPhysioSource={activeTab === "physio"}
          />
        </Box>
      )}

      {/* Loading state, empty states, or video grid */}
      {(isLoading || isFetching) && displayVideos.length === 0 ? (
        renderLoadingState()
      ) : !isLoading && !isFetching && displayVideos.length === 0 ? (
        // Empty state: no videos found or no matches for active filter
        <Box sx={{ textAlign: "center", py: 8 }}>
          <FilterIcon
            sx={{ fontSize: 64, color: "rgba(255, 255, 255, 0.3)", mb: 2 }}
          />
          <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
            {activeTab === "physio"
              ? t("noPhysioVideos") || "No physio videos available"
              : hasPreferenceFilter
                ? t("noVideosMatchFilter")
                : t("noVideosAvailable")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.7)", mb: 3 }}
          >
            {hasPreferenceFilter && activeTab === "all"
              ? t("tryDifferentFilter")
              : t("checkBackLater")}
          </Typography>
          {hasPreferenceFilter && activeTab === "all" && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={resetFilters}
              sx={{ color: "white", borderColor: "white" }}
            >
              {t("clearFilters")}
            </Button>
          )}
        </Box>
      ) : (
        // Video grid
        renderVideoGrid()
      )}

      {/* Refresh FAB button (visible when not loading) */}
      {!isLoading && !isFetching && (
        <Tooltip title={t("refresh") || "Refresh"}>
          <Fab
            color="primary"
            aria-label="refresh"
            onClick={refetch}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <RefreshIcon />
          </Fab>
        </Tooltip>
      )}
    </Container>
  );
};

export default VideoLibraryComponent;
